import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { AppDispatch } from './store';
import type { CoverageArea, Provider } from './models/provider.model';
import type { PagedList } from './models/paged-list.model';
import type { SortModel } from './models/sort-model.model';
import type { TripLegProviderNoteType } from './models/note.model';
import {
  ProviderCallOutcomeBC,
  ProviderCallOutcomeRequest,
  ProviderCallOutcomeResponse,
  RideshareOutboundPhoneNumber,
  RideshareProvider,
} from './models/provider-call-outcome.model';

import * as uiSlice from './ui.slice';
import * as triplegSlice from './tripleg.slice';
import reservationService from '../services/reservationService';
import triplegService from '../services/triplegService';
import providerService from '../services/providerService';
import { TripLegStatus } from './models/trip-leg-status.model';
import { AssignTripLeg } from './models/trip-leg-assign.model';

interface ProviderState {
  transportApiInfo: any;
  selectedFilter: string;
  searchFilter: string;
  // searchDriverIdFilter: string;
  // searchAreaFilter: string;
  // driverSearchFilter: string;
  // vehicleSearchFilter: string;
  page: number;
  pageSize: number;
  sortModel: SortModel | null;
  loadingProviderList: boolean;
  loadedProviderList: boolean;
  providerList: PagedList<Provider>;
  loadingProvider: boolean;
  loadedProvider: boolean;
  provider: Provider | null;
  providers: Provider[];
  requestedProvider: Provider | null;
  internalNoteList: any;
  providerNoteList: any;
  savingProviderCallOutcome: boolean;
  savedProviderCallOutcome: boolean;
  acceptedProviderCallOutcome: boolean;
  manuallyAssigningTripLegs: boolean;
  manuallyAssignedTripLegs: boolean;
  coverageAreas: CoverageArea[];
  loadingCoverageAreas: boolean;
  loadedCoverageAreas: boolean;
  loadedProviderListError: string | null;
  providerCallOutcomes: PagedList<ProviderCallOutcomeResponse>;
  loadingProviderCallOutcomes: boolean;
  loadedProviderCallOutcomes: boolean;
  rideshareOutboundPhoneNumbers: RideshareOutboundPhoneNumber[];
  updatingRideshareOutboundPhoneNumbers: boolean;
  pendingReassignTripLeg: AssignTripLeg | null;
}

export const initialState: ProviderState = {
  transportApiInfo: null,
  selectedFilter: 'ctt0&active0',
  searchFilter: '',
  // searchDriverIdFilter: '',
  // searchAreaFilter: '',
  // driverSearchFilter: '',
  // vehicleSearchFilter: '',
  page: 1,
  pageSize: 10,
  sortModel: null,
  loadingProviderList: false,
  loadedProviderList: false,
  providerList: { itemCount: 0, currentPage: 1, totalPages: 0, items: [] },
  loadingProvider: false,
  loadedProvider: false,
  provider: null,
  providers: [],
  requestedProvider: null,
  internalNoteList: null,
  providerNoteList: null,
  savingProviderCallOutcome: false,
  savedProviderCallOutcome: false,
  acceptedProviderCallOutcome: false,
  manuallyAssigningTripLegs: false,
  manuallyAssignedTripLegs: false,
  coverageAreas: [],
  loadingCoverageAreas: false,
  loadedCoverageAreas: false,
  loadedProviderListError: null,
  providerCallOutcomes: {
    itemCount: 0,
    currentPage: 1,
    totalPages: 0,
    items: [],
  },
  loadingProviderCallOutcomes: false,
  loadedProviderCallOutcomes: false,
  rideshareOutboundPhoneNumbers: [],
  updatingRideshareOutboundPhoneNumbers: false,
  pendingReassignTripLeg: null,
};

export const slice = createSlice({
  name: 'provider',
  initialState,
  reducers: {
    setTransportApiInfo: (
      state: ProviderState,
      action: { type: string; payload: string },
    ) => {
      state.transportApiInfo = action.payload;
    },
    setSelectedFilter: (
      state: ProviderState,
      action: { type: string; payload: string },
    ) => {
      state.selectedFilter = action.payload;
      state.page = 1;
    },
    setSearchFilter: (
      state: ProviderState,
      action: { type: string; payload: string },
    ) => {
      state.searchFilter = action.payload;
      state.page = 1;
    },
    // setSearchDriverIdFilter: (
    //   state: ProviderState,
    //   action: { type: string; payload: string },
    // ) => {
    //   state.searchDriverIdFilter = action.payload;
    //   state.page = 1;
    // },
    // setSearchAreaFilter: (
    //   state: ProviderState,
    //   action: { type: string; payload: string },
    // ) => {
    //   state.searchAreaFilter = action.payload;
    //   state.page = 1;
    // },
    // setDriverSearchFilter: (
    //   state: ProviderState,
    //   action: { type: string; payload: string },
    // ) => {
    //   state.driverSearchFilter = action.payload;
    //   state.page = 1;
    // },
    // setVehicleSearchFilter: (
    //   state: ProviderState,
    //   action: { type: string; payload: string },
    // ) => {
    //   state.vehicleSearchFilter = action.payload;
    //   state.page = 1;
    // },
    setPage: (
      state: ProviderState,
      action: { type: string; payload: number },
    ) => {
      state.page = action.payload;
    },
    setPageSize: (
      state: ProviderState,
      action: { type: string; payload: number },
    ) => {
      state.pageSize = action.payload;
    },
    setSortModel: (
      state: ProviderState,
      action: PayloadAction<SortModel | null>,
    ) => {
      state.sortModel = action.payload;
    },
    setLoadingProviderList: (
      state: ProviderState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingProviderList = action.payload;
    },
    setLoadedProviderList: (
      state: ProviderState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedProviderList = action.payload;
    },
    setLoadedProviderListError: (
      state: ProviderState,
      action: { type: string; payload: string | null },
    ) => {
      state.loadedProviderListError = action.payload;
    },
    setProviderList: (
      state: ProviderState,
      action: { type: string; payload: PagedList<Provider> },
    ) => {
      state.providerList = action.payload;
    },
    setLoadingProvider: (
      state: ProviderState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingProvider = action.payload;
    },
    setLoadedProvider: (
      state: ProviderState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedProvider = action.payload;
    },
    setSavingProviderCallOutcome: (
      state: ProviderState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingProviderCallOutcome = action.payload;
    },
    setSavedProviderCallOutcome: (
      state: ProviderState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedProviderCallOutcome = action.payload;
    },
    setAcceptedProviderCallOutcome: (
      state: ProviderState,
      action: { type: string; payload: boolean },
    ) => {
      state.acceptedProviderCallOutcome = action.payload;
    },
    setProvider: (
      state: ProviderState,
      action: { type: string; payload: Provider | null },
    ) => {
      state.provider = action.payload ? { ...action.payload } : null;
      state.loadingProvider = false;
      state.loadedProvider = true;
    },
    upsertProviders: (
      state: ProviderState,
      action: { type: string; payload: Provider },
    ) => {
      const providers = [...state.providers];
      const idx = providers.findIndex(
        (p) =>
          p.transportationProviderID ===
          action.payload.transportationProviderID,
      );
      if (idx >= 0) {
        providers.splice(idx, 1, action.payload);
        state.providers = providers;
      } else {
        providers.push(action.payload);
        state.providers = providers;
      }
    },
    setRequestedProvider: (
      state: ProviderState,
      action: { type: string; payload: Provider | null },
    ) => {
      state.requestedProvider = action.payload;
    },
    setInternalNoteList: (
      state: ProviderState,
      action: PayloadAction<TripLegProviderNoteType[]>,
    ) => {
      state.internalNoteList = action.payload;
    },
    upsertInternalNote: (
      state: ProviderState,
      action: { type: string; payload: TripLegProviderNoteType },
    ) => {
      if (!state.internalNoteList) {
        state.internalNoteList = [];
      }
      const foundIdx = state.internalNoteList.findIndex(
        (note: TripLegProviderNoteType) =>
          note.tripLegInternalNoteID ===
          action.payload.tripLegInternalNote?.tripLegInternalNoteID,
      );
      const newNote = {
        ...action.payload.tripLegInternalNote,
        createdByUser: action.payload.createdByUser,
      };
      if (foundIdx >= 0) {
        state.internalNoteList[foundIdx] = {
          ...state.internalNoteList[foundIdx],
          ...action.payload.tripLegInternalNote,
          createdByUser: action.payload.createdByUser,
        };
        return;
      } else {
        state.internalNoteList.push(newNote);
      }
    },
    upsertProviderNote: (
      state: ProviderState,
      action: { type: string; payload: TripLegProviderNoteType },
    ) => {
      const idx = state.providerNoteList.findIndex(
        (note: TripLegProviderNoteType) =>
          note.tripLegInternalNoteID ===
          action.payload.tripLegInternalNote?.tripLegInternalNoteID,
      );
      if (idx >= 0) {
        state.providerNoteList[idx] = {
          ...state.providerNoteList[idx],
          ...action.payload.tripLegInternalNote,
          createdByUser: action.payload.createdByUser,
        };
      } else {
        state.providerNoteList.push(action.payload);
      }
    },
    removeProviderNote: (
      state: ProviderState,
      action: { type: string; payload: TripLegProviderNoteType },
    ) => {
      const idx = state.providerNoteList.findIndex(
        (note: TripLegProviderNoteType) =>
          note.tripLegInternalNoteID === action.payload.tripLegInternalNoteID,
      );
      if (idx >= 0) {
        state.providerNoteList.splice(idx, 1);
      }
    },
    setManuallyAssigningTripLegs: (
      state: ProviderState,
      action: { type: string; payload: boolean },
    ) => {
      state.manuallyAssigningTripLegs = action.payload;
    },
    setManuallyAssignedTripLegs: (
      state: ProviderState,
      action: { type: string; payload: boolean },
    ) => {
      state.manuallyAssignedTripLegs = action.payload;
    },
    setCoverageAreas: (
      state: ProviderState,
      action: { type: string; payload: CoverageArea[] },
    ) => {
      state.coverageAreas = action.payload;
    },
    setLoadingCoverageAreas: (
      state: ProviderState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingCoverageAreas = action.payload;
    },
    setLoadedCoverageAreas: (
      state: ProviderState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedCoverageAreas = action.payload;
    },
    setProviderCallOutcomes: (
      state: ProviderState,
      action: { type: string; payload: PagedList<ProviderCallOutcomeResponse> },
    ) => {
      state.providerCallOutcomes = action.payload;
    },
    setLoadingProviderCallOutcomes: (
      state: ProviderState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingProviderCallOutcomes = action.payload;
    },
    setLoadedProviderCallOutcomes: (
      state: ProviderState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedProviderCallOutcomes = action.payload;
    },
    setRideshareOutboundPhoneNumbers: (
      state: ProviderState,
      action: { type: string; payload: RideshareOutboundPhoneNumber[] },
    ) => {
      state.rideshareOutboundPhoneNumbers = action.payload;
    },
    setUpdatingRideshareOutboundPhoneNumbers: (
      state: ProviderState,
      action: { type: string; payload: boolean },
    ) => {
      state.updatingRideshareOutboundPhoneNumbers = action.payload;
    },
    setPendingReassignTripLeg: (
      state: ProviderState,
      action: { type: string; payload: AssignTripLeg | null },
    ) => {
      state.pendingReassignTripLeg = action.payload;
    },
  },
});

export const { setSelectedFilter } = slice.actions;
export const { setSearchFilter } = slice.actions;
export const { setPage } = slice.actions;
export const { setPageSize } = slice.actions;
export const { setSortModel } = slice.actions;
export const { setLoadingProviderList } = slice.actions;
export const { setLoadedProviderList } = slice.actions;
export const { setLoadedProviderListError } = slice.actions;
export const { setProviderList } = slice.actions;
export const { setLoadingProvider } = slice.actions;
export const { setLoadedProvider } = slice.actions;
export const { setSavingProviderCallOutcome } = slice.actions;
export const { setSavedProviderCallOutcome } = slice.actions;
export const { setAcceptedProviderCallOutcome } = slice.actions;
export const { setProvider } = slice.actions;
export const { upsertProviders } = slice.actions;
export const { setRequestedProvider } = slice.actions;
export const { setTransportApiInfo } = slice.actions;
export const { setInternalNoteList } = slice.actions;
export const { upsertInternalNote } = slice.actions;
export const { upsertProviderNote } = slice.actions;
export const { removeProviderNote } = slice.actions;
export const { setManuallyAssigningTripLegs } = slice.actions;
export const { setManuallyAssignedTripLegs } = slice.actions;
export const { setCoverageAreas } = slice.actions;
export const { setLoadingCoverageAreas } = slice.actions;
export const { setLoadedCoverageAreas } = slice.actions;
export const { setProviderCallOutcomes } = slice.actions;
export const { setLoadingProviderCallOutcomes } = slice.actions;
export const { setLoadedProviderCallOutcomes } = slice.actions;
export const { setRideshareOutboundPhoneNumbers } = slice.actions;
export const { setUpdatingRideshareOutboundPhoneNumbers } = slice.actions;
export const { setPendingReassignTripLeg } = slice.actions;

export const fetchTransportApiInfoAsync = () => (dispatch: AppDispatch) => {
  providerService.getTransportApiInfo().subscribe({
    next: (apiInfo: any) => {
      dispatch(setTransportApiInfo(apiInfo));
    },
    error: (err) => {
      uiSlice.setErrorMessage(err.message || 'snackbar.apiVersionError');
    },
  });
};

export const fetchCoverageAreasAsync =
  (stateCode: string, zipCode: string) => (dispatch: AppDispatch) => {
    dispatch(setLoadingCoverageAreas(true));
    dispatch(setLoadedCoverageAreas(false));
    providerService.getCoverageAreas(stateCode, zipCode).subscribe({
      next: (coverageAreas) => {
        dispatch(setCoverageAreas(coverageAreas.coverageAreas));
        dispatch(setLoadingCoverageAreas(false));
        dispatch(setLoadedCoverageAreas(true));
      },
      error: (err) => {
        dispatch(setLoadingCoverageAreas(false));
        dispatch(uiSlice.setErrorMessage(err.message));
      },
    });
  };

export const fetchProviderAsync =
  (providerID: number) => (dispatch: AppDispatch) => {
    dispatch(setLoadingProvider(true));
    dispatch(setLoadedProvider(false));
    providerService.getProvider(providerID).subscribe({
      next: (provider) => {
        dispatch(setLoadingProvider(false));
        dispatch(setLoadedProvider(true));
        dispatch(setProvider(provider));
        dispatch(upsertProviders(provider));
      },
      error: (err) => {
        dispatch(setLoadingProvider(false));
        dispatch(uiSlice.setErrorMessage(err.message));
      },
    });
  };

export const fetchRequestedProviderAsync =
  (providerID: number) => (dispatch: AppDispatch) => {
    providerService.getProvider(providerID).subscribe({
      next: (provider) => {
        dispatch(setRequestedProvider(provider));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
      },
    });
  };

export const fetchProviderListAsync =
  ({
    pageSize,
    page,
    filter,
    search,
    sortModel,
    tiers,
    transportationProviderIDs,
  }: {
    pageSize: number;
    page: number;
    filter: string;
    search: string;
    sortModel: SortModel | null;
    tiers?: string;
    transportationProviderIDs?: number[];
  }) =>
  (dispatch: AppDispatch) => {
    dispatch(setLoadingProviderList(true));
    dispatch(setLoadedProviderList(false));
    providerService
      .findProviders({
        pageSize,
        page,
        filter,
        search,
        sortModel,
        tiers,
        transportationProviderIDs,
      })
      .subscribe({
        next: (providerList) => {
          dispatch(setLoadingProviderList(false));
          dispatch(setLoadedProviderList(true));
          dispatch(setProviderList(providerList));
        },
        error: (err) => {
          dispatch(setLoadingProviderList(false));
          dispatch(setLoadedProviderListError(err.message));
          dispatch(uiSlice.setErrorMessage('snackbar.providerListLoadFailed'));
        },
      });
  };

export const refetchProviderListAsync =
  () => (dispatch: AppDispatch, getState: Function) => {
    const state = getState();
    const providerState = state.provider;
    dispatch(
      fetchProviderListAsync({
        pageSize: providerState.pageSize,
        page: providerState.page,
        filter: providerState.selectedFilter,
        search: providerState.searchFilter,
        sortModel: providerState.sortModel,
        tiers: providerState.tiers,
      }),
    );
  };

// Guards every commit to the shared internalNoteList against two races:
// - activeInternalNoteLegID is the leg the user is currently viewing, set
//   only by fetchInternalNoteAsync (the tab-selection path, called by
//   TripLegDetails/TripLegActivity/TripLegDialog). addInternalNoteAsync's
//   post-add refresh targets whatever leg the note was posted to, which may
//   no longer be on screen by the time it resolves, so it checks against
//   this instead of claiming it — otherwise a refresh for a leg the user
//   navigated away from could overwrite the leg now being viewed.
// - internalNoteFetchSeq is a shared latest-wins token so an older response
//   for that SAME leg (e.g. navigating back to a leg already in flight)
//   can't clobber a newer one.
let activeInternalNoteLegID: string | null = null;
let internalNoteFetchSeq = 0;

const loadInternalNotes =
  (tripLegReferenceID: string, tripLegStatus: string | undefined) =>
  (dispatch: AppDispatch) => {
    const seq = ++internalNoteFetchSeq;
    const isCurrent = () =>
      seq === internalNoteFetchSeq &&
      tripLegReferenceID === activeInternalNoteLegID;

    triplegService.getInternalNotes({ tripLegReferenceID }).subscribe({
      next: (noteList) => {
        if (!isCurrent()) return; // stale, or no longer the viewed leg
        dispatch(setInternalNoteList(noteList));
      },
      // Internal notes are supplementary to the edit trip leg dialog (e.g.
      // gurney measurements), so a failed fetch must not surface as an
      // uncaught RxJS error or block the rest of the dialog from loading.
      error: (err) => {
        if (!isCurrent()) return; // stale, or no longer the viewed leg
        console.error('Failed to fetch trip leg internal notes', err);
        dispatch(setInternalNoteList([]));
      },
    });
  };

export const addInternalNoteAsync =
  (
    tripLegReferenceID: string,
    tripLegInternalNoteComment: string,
    tripLegStatus: string | undefined,
  ) =>
  (dispatch: AppDispatch) => {
    triplegService
      .createInternalNote({ tripLegReferenceID, tripLegInternalNoteComment })
      .subscribe({
        next: () => {
          if (tripLegReferenceID === activeInternalNoteLegID) {
            dispatch(loadInternalNotes(tripLegReferenceID, tripLegStatus));
          }
          dispatch(uiSlice.setSuccessMessage('snackbar.internalNoteSuccess'));
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.errors[0].message));
        },
      });
  };

export const fetchInternalNoteAsync =
  (tripLegReferenceID: string, tripLegStatus: string | undefined) =>
  (dispatch: AppDispatch) => {
    activeInternalNoteLegID = tripLegReferenceID;
    dispatch(loadInternalNotes(tripLegReferenceID, tripLegStatus));
  };

export const fetchProvidersCallOutcomesAsync =
  ({
    tripLegReferenceID,
    search,
    page,
    pageSize,
    sortModel,
    hasCallOutcome,
  }: ProviderCallOutcomeRequest) =>
  (dispatch: AppDispatch) => {
    dispatch(setLoadingProviderCallOutcomes(true));
    dispatch(setLoadedProviderCallOutcomes(false));
    providerService
      .getProviderCallOutcomes({
        transportationProviderTier: '1,2,3',
        tripLegReferenceID,
        search,
        page,
        pageSize,
        sortModel,
        hasCallOutcome,
      })
      .subscribe({
        next: (providerCallOutcomes) => {
          dispatch(setProviderCallOutcomes(providerCallOutcomes));
          dispatch(setLoadingProviderCallOutcomes(false));
          dispatch(setLoadedProviderCallOutcomes(true));
        },
        error: (err) => {
          dispatch(setLoadingProviderCallOutcomes(false));
          dispatch(setLoadedProviderCallOutcomes(false));
          dispatch(uiSlice.setErrorMessage(err.message));
        },
      });
  };

export const fetchRideshareOutboundPhoneNumbers =
  () => (dispatch: AppDispatch) => {
    providerService.getRideshareOutboundPhoneNumbers().subscribe({
      next: (phoneNumbers: RideshareOutboundPhoneNumber[]) => {
        dispatch(setRideshareOutboundPhoneNumbers(phoneNumbers));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
      },
    });
  };

export const updateRideshareOutboundPhoneNumbers =
  (phoneNumber: RideshareOutboundPhoneNumber) => (dispatch: AppDispatch) => {
    dispatch(setUpdatingRideshareOutboundPhoneNumbers(true));
    providerService.updateRideshareOutboundPhoneNumbers(phoneNumber).subscribe({
      next: () => {
        dispatch(setUpdatingRideshareOutboundPhoneNumbers(false));
        dispatch(
          uiSlice.setSuccessMessage(
            'snackbar.rideshareOutboundPhoneNumbersUpdatedSuccess',
          ),
        );
        dispatch(fetchRideshareOutboundPhoneNumbers());
      },
      error: (err) => {
        dispatch(setUpdatingRideshareOutboundPhoneNumbers(false));
        dispatch(uiSlice.setErrorMessage(err.message));
      },
    });
  };

export const deleteRideshareOutboundPhoneNumbers =
  (rideshareProvider: RideshareProvider) => (dispatch: AppDispatch) => {
    dispatch(setUpdatingRideshareOutboundPhoneNumbers(true));
    providerService
      .deleteRideshareOutboundPhoneNumbers(rideshareProvider)
      .subscribe({
        next: () => {
          dispatch(setUpdatingRideshareOutboundPhoneNumbers(false));
          dispatch(
            uiSlice.setSuccessMessage(
              'snackbar.rideshareOutboundPhoneNumbersUpdatedSuccess',
            ),
          );
          dispatch(fetchRideshareOutboundPhoneNumbers());
        },
        error: (err) => {
          dispatch(setUpdatingRideshareOutboundPhoneNumbers(false));
          dispatch(uiSlice.setErrorMessage(err.message));
        },
      });
  };

export const manuallyAssignTripLegToProviderAsync =
  (tripLegInfo: AssignTripLeg) => (dispatch: AppDispatch) => {
    dispatch(setSavingProviderCallOutcome(true));
    dispatch(setSavedProviderCallOutcome(false));
    triplegService
      .manuallyAssignTripLegToProvider({
        ...tripLegInfo,
      })
      .subscribe({
        next: () => {
          dispatch(
            uiSlice.setSuccessMessage('snackbar.tripLegAssignedSuccess'),
          );
          dispatch(setSavingProviderCallOutcome(false));
          dispatch(setSavedProviderCallOutcome(true));
        },
        error: (err) => {
          dispatch(setSavingProviderCallOutcome(false));
          dispatch(uiSlice.setErrorMessage(err.message));
        },
      });
  };

export const manuallyReassignTripLegToProviderAsync =
  (tripLegInfo: AssignTripLeg) => (dispatch: AppDispatch) => {
    dispatch(setSavingProviderCallOutcome(true));
    dispatch(setSavedProviderCallOutcome(false));
    reservationService
      .rejectTripLeg({
        passengerUUID: tripLegInfo.passengerUUID,
        tripLegReferenceID: tripLegInfo.tripLegReferenceID!,
        tripLegRejectedReasonBC: 'REASSIGN',
      })
      .subscribe({
        next: () => {
          dispatch(setPendingReassignTripLeg(tripLegInfo));
        },
        error: (err) => {
          dispatch(setSavingProviderCallOutcome(false));
          dispatch(uiSlice.setErrorMessage(err.message));
        },
      });
  };

export const completePendingReassignTripLegAsync =
  (tripLegReferenceID: string) =>
  (dispatch: AppDispatch, getState: Function) => {
    const state = getState();
    const pendingReassign = state.provider.pendingReassignTripLeg;

    if (
      pendingReassign &&
      pendingReassign.tripLegReferenceID === tripLegReferenceID
    ) {
      dispatch(setPendingReassignTripLeg(null));
      dispatch(manuallyAssignTripLegToProviderAsync(pendingReassign));
    }
  };

export const saveProviderCallOutcomeAsync =
  ({
    tripLegReferenceIDs,
    callOutcomeBC,
    callOutcomeID,
    callOutcomeRejectReasonID,
    rejectedReason,
    rejectedReasonBC,
    serviceAssurance,
    transportationProviderID,
    clientTransportationServiceUUID,
    providerTransportationServiceUUID,
    coverageAreaID,
    passengerUUID,
    clearException,
  }: {
    tripLegReferenceIDs: string[];
    callOutcomeBC: string;
    callOutcomeID: number;
    callOutcomeRejectReasonID?: number;
    rejectedReason?: string;
    rejectedReasonBC?: string;
    serviceAssurance?: string;
    transportationProviderID: number;
    clientTransportationServiceUUID: string;
    providerTransportationServiceUUID: string;
    coverageAreaID: number;
    passengerUUID: string;
    clearException?: boolean;
  }) =>
  (dispatch: AppDispatch) => {
    dispatch(setSavingProviderCallOutcome(true));
    dispatch(setManuallyAssigningTripLegs(true));
    dispatch(setSavedProviderCallOutcome(false));
    dispatch(setManuallyAssignedTripLegs(false));
    // biome-ignore lint/complexity/noForEach: <explanation>
    tripLegReferenceIDs.forEach((tripLegReferenceID) => {
      triplegService
        .saveProviderCallOutcome({
          tripLegReferenceID,
          transportationProviderID,
          callOutcomeID,
          callOutcomeRejectReasonID,
          rejectedReason,
          serviceAssurance,
        })
        .subscribe({
          next: (response) => {
            dispatch(setSavingProviderCallOutcome(false));
            dispatch(setSavedProviderCallOutcome(true));
            if (
              callOutcomeBC === ProviderCallOutcomeBC.Accepted ||
              callOutcomeBC === ProviderCallOutcomeBC.Rejected
            ) {
              triplegService
                .manuallyAssignTripLegToProvider({
                  tripLegReferenceID,
                  status:
                    callOutcomeBC === ProviderCallOutcomeBC.Accepted
                      ? TripLegStatus.Accepted
                      : TripLegStatus.Rejected,
                  transportationProviderID,
                  clientTransportationServiceUUID,
                  providerTransportationServiceUUID,
                  passengerUUID,
                  coverageAreaID,
                  tripLegRejectedReasonBC: rejectedReasonBC,
                })
                .subscribe({
                  next: () => {
                    dispatch(setManuallyAssignedTripLegs(true));
                    dispatch(setManuallyAssigningTripLegs(false));
                    switch (callOutcomeBC) {
                      case ProviderCallOutcomeBC.Accepted:
                        dispatch(
                          uiSlice.setSuccessMessage(
                            'snackbar.findProviderAcceptedSuccess',
                          ),
                        );
                        if (clearException) {
                          dispatch(
                            triplegSlice.setSavingTripLegException(true),
                          );
                          dispatch(
                            triplegSlice.setSavedTripLegException(false),
                          );
                          triplegService
                            .clearTripLegException({ tripLegReferenceID })
                            .subscribe({
                              next: () => {
                                dispatch(
                                  triplegSlice.setSavingTripLegException(false),
                                );
                                dispatch(
                                  triplegSlice.setSavedTripLegException(true),
                                );
                                dispatch(setAcceptedProviderCallOutcome(true));
                              },
                              error: (err) => {
                                dispatch(
                                  triplegSlice.setSavingTripLegException(false),
                                );
                                dispatch(
                                  triplegSlice.setSavedTripLegException(true),
                                );
                                dispatch(uiSlice.setErrorMessage(err.message));
                                dispatch(setAcceptedProviderCallOutcome(true));
                              },
                            });
                        } else {
                          dispatch(setAcceptedProviderCallOutcome(true));
                        }
                        break;
                      case ProviderCallOutcomeBC.Rejected:
                        dispatch(
                          uiSlice.setSuccessMessage(
                            'snackbar.findProviderRejectedSuccess',
                          ),
                        );
                        break;
                    }
                  },
                  error: (err) => {
                    dispatch(setManuallyAssigningTripLegs(false));
                    dispatch(uiSlice.setErrorMessage(err.message));
                  },
                });
            } else {
              switch (callOutcomeBC) {
                case ProviderCallOutcomeBC.NoAnswer:
                  dispatch(
                    uiSlice.setSuccessMessage(
                      'snackbar.findProviderNoAnswerSuccess',
                    ),
                  );
                  break;
                default:
                  dispatch(
                    uiSlice.setSuccessMessage(
                      'snackbar.providerCallOutcomeSuccess',
                    ),
                  );
                  break;
              }
            }
          },
          error: (err) => {
            dispatch(setSavingProviderCallOutcome(false));
            dispatch(setSavedProviderCallOutcome(false));
            dispatch(
              uiSlice.setErrorMessage(
                `snackbar.findProviderFailed: ${err.message}`,
              ),
            );
          },
        });
    });
  };

export const selectSelectedFilter = (state: { provider: ProviderState }) =>
  state.provider.selectedFilter;
export const selectSearchFilter = (state: { provider: ProviderState }) =>
  state.provider.searchFilter;
export const selectPage = (state: { provider: ProviderState }) =>
  state.provider.page;
export const selectPageSize = (state: { provider: ProviderState }) =>
  state.provider.pageSize;
export const selectSortModel = (state: { provider: ProviderState }) =>
  state.provider.sortModel;
export const selectLoadingProviderList = (state: { provider: ProviderState }) =>
  state.provider.loadingProviderList;
export const selectLoadedProviderList = (state: { provider: ProviderState }) =>
  state.provider.loadedProviderList;
export const selectLoadedProviderListError = (state: {
  provider: ProviderState;
}) => state.provider.loadedProviderListError;
export const selectProviderList = (state: { provider: ProviderState }) =>
  state.provider.providerList;
export const selectLoadingProvider = (state: { provider: ProviderState }) =>
  state.provider.loadingProvider;
export const selectLoadedProvider = (state: { provider: ProviderState }) =>
  state.provider.loadedProvider;
export const selectProvider = (state: { provider: ProviderState }) =>
  state.provider.provider;
export const selectSavingProviderCallOutcome = (state: {
  provider: ProviderState;
}) => state.provider.savingProviderCallOutcome;
export const selectSavedProviderCallOutcome = (state: {
  provider: ProviderState;
}) => state.provider.savedProviderCallOutcome;
export const selectAcceptedProviderCallOutcome = (state: {
  provider: ProviderState;
}) => state.provider.acceptedProviderCallOutcome;
export const selectProviderByID = (
  state: { provider: ProviderState },
  id: number,
) => state.provider.providers.find((p) => p.transportationProviderID === id);
export const selectRequestedProvider = (state: { provider: ProviderState }) =>
  state.provider.requestedProvider;
export const selectTransportApiInfo = (state: { provider: ProviderState }) =>
  state.provider?.transportApiInfo;
export const selectInternalNoteList = (state: { provider: ProviderState }) =>
  state.provider.internalNoteList;
export const selectCoverageAreas = (state: { provider: ProviderState }) =>
  state.provider.coverageAreas;
export const selectLoadingCoverageAreas = (state: {
  provider: ProviderState;
}) => state.provider.loadingCoverageAreas;
export const selectLoadedCoverageAreas = (state: { provider: ProviderState }) =>
  state.provider.loadedCoverageAreas;
export const selectManuallyAssigningTripLegs = (state: {
  provider: ProviderState;
}) => state.provider.manuallyAssigningTripLegs;
export const selectManuallyAssignedTripLegs = (state: {
  provider: ProviderState;
}) => state.provider.manuallyAssignedTripLegs;
export const selectProviderCallOutcomes = (state: {
  provider: ProviderState;
}) => state.provider.providerCallOutcomes;
export const selectLoadingProviderCallOutcomes = (state: {
  provider: ProviderState;
}) => state.provider.loadingProviderCallOutcomes;
export const selectLoadedProviderCallOutcomes = (state: {
  provider: ProviderState;
}) => state.provider.loadedProviderCallOutcomes;
export const selectRideshareOutboundPhoneNumbers = (state: {
  provider: ProviderState;
}) => state.provider.rideshareOutboundPhoneNumbers;
export const selectUpdatingRideshareOutboundPhoneNumbers = (state: {
  provider: ProviderState;
}) => state.provider.updatingRideshareOutboundPhoneNumbers;
export const selectPendingReassignTripLeg = (state: {
  provider: ProviderState;
}) => state.provider.pendingReassignTripLeg;

export default slice.reducer;
